/**
 * Friendly editor model for Service Line Task schedules.
 *
 * This is the persisted source of truth for what the user picked in the
 * schedule editor (docs/plans/planned-task-schedule.md §2/§4). The UI
 * reopens this model directly — RRULE strings are compiled FROM it on save
 * and are never parsed back.
 *
 * All dates are local calendar dates in the line's timezone, formatted
 * YYYY-MM-DD. Never BSON Date / JS Date instants.
 */

export type ScheduleKind = 'perVisit' | 'calendar' | 'afterService' | 'dates';

export type ScheduleRepeat = 'daily' | 'weekly' | 'fortnightly' | 'monthly' | 'quarterly' | 'yearly';

/** The two halves of an anchored fortnight: week A starts on the effective-from week, week B one week later. */
export type ScheduleWeekCycle = 'A' | 'B';

/**
 * 'workingDays' is the odd one out: every other preset names a fixed day set, but this one means
 * "whatever the line's resolved working week is" (line → plan → Mon–Fri legacy fallback). It compiles to
 * no RRULE at all — the day set is context-dependent, so there is no static rule to write — and is
 * evaluated against `ctx.workWeek` instead. It is also what an ABSENT schedule resolves to.
 */
export type ScheduleDailyPreset = 'everyDay' | 'everyNDays' | 'weekdays' | 'sixDay' | 'weekend' | 'workingDays';

export type ScheduleMonthlyOn = 'dayOfMonth' | 'nthWeekday';

export type ScheduleNth = 1 | 2 | 3 | 4 | 'last';

/** ISO weekday numbers, 1 = Monday … 7 = Sunday (WKST=MO everywhere). */
export type ScheduleWeekday = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export type ScheduleNonWorkingDayPolicy = 'keep' | 'skip' | 'next' | 'previous' | 'nearest';

export type ScheduleEnds = { type: 'never' } | { type: 'onDate'; date: string } | { type: 'afterCount'; count: number };

export interface SchedulePerVisitRule {
    /** 1 = every visit, 2 = every 2nd visit, … */
    nth: number;
}

/**
 * What an EARLY completion does to the next due date (WBS-10921 "Compliance Windows").
 *
 * 'completion' re-anchors the chain to the day the work was actually done: service a 12-month regime in
 * November and every later year falls in November. 'anniversary' keeps the original cycle regardless —
 * servicing early to dodge a holiday period does not permanently drag the contract date forward.
 *
 * Absent means 'completion', which is the behaviour every stored schedule already has.
 */
export type ScheduleAfterServiceAnchor = 'completion' | 'anniversary';

/**
 * The unit a leeway is counted in. 'businessDays' skips Saturdays, Sundays and the line's public
 * holidays, which is how contract tolerances are written ("±2 business days", contract clause 5.3.3).
 */
export type ScheduleLeewayUnit = 'days' | 'businessDays' | 'months' | 'years';

/**
 * A tolerance either side of a statutory due date, as a duration.
 *
 * `earlyLeeway` and `lateLeeway` together define the compliance window `[dueDate − early, dueDate + late]`.
 * Work inside it counts legally and is not an exception; work before it opens either wastes a service or
 * does not count, so schedulers warn, and nothing is in breach until it closes.
 */
export interface ScheduleLeeway {
    value: number;
    unit: ScheduleLeewayUnit;
}

/**
 * The compliance tolerance a schedule rule may carry: after-service rules and every calendar repeat
 * except daily. Both sides absent means no leeway, so the due date is the whole window — the meaning
 * every stored schedule had before leeway existed.
 */
export interface ScheduleLeewayWindow {
    /**
     * Optional early-service window before the due date. Absent means no leeway: the due date is both
     * the deadline and the earliest countable date.
     */
    earlyLeeway?: ScheduleLeeway;
    /**
     * Optional tolerance after the due date. Absent means none: the due date is the deadline, and
     * compliance breaches the day after it.
     */
    lateLeeway?: ScheduleLeeway;
}

export interface ScheduleAfterServiceRule extends ScheduleLeewayWindow {
    interval: number;
    unit: 'days' | 'months' | 'years';
    /**
     * @deprecated Legacy chain seed, kept only so stored values keep evaluating. The chain now seeds
     * from `ctx.lastCompleted` first and falls back to the line's effectiveFrom; `firstDue` sits
     * between the two and is no longer written by the editor. A data migration removes stored values.
     * YYYY-MM-DD.
     */
    firstDue?: string;
    /**
     * What an early completion does to the NEXT due date. Absent means 'completion' — the historical
     * behaviour, preserved so no stored schedule changes meaning.
     */
    anchor?: ScheduleAfterServiceAnchor;
}

/**
 * The explicit date list: "these days and no others". No pattern, no ends clause — the list IS the
 * end, which is why this editor carries neither `ends` nor an interval.
 *
 * Stored sorted ascending and deduplicated, at most SCHEDULE_DATES_MAX entries. The writer
 * (the API guard) owns that canonicalisation; `validateTaskSchedule` only reports on it.
 */
export interface ScheduleDatesRule {
    /** Local calendar dates, YYYY-MM-DD. Sorted ascending, unique, 1..SCHEDULE_DATES_MAX entries. */
    dates: string[];
    /**
     * What happens when a listed date lands outside the working week or on a holiday. Default 'keep'.
     * Mirrors the calendar editor's field; it lives here too because a hand-picked list can still land
     * on a closure the author did not know about.
     */
    nonWorkingDayPolicy?: ScheduleNonWorkingDayPolicy;
}

/**
 * Upper bound on an explicit date list. A schedule that needs more than two dozen hand-picked days is
 * a pattern someone has typed out by hand, and the editor pushes them to a calendar repeat instead.
 */
export const SCHEDULE_DATES_MAX = 24;

/**
 * A calendar repeat. Its leeway (every repeat but daily) is measured from each occurrence date, so a
 * weekly task with one business day either side may be done from the Friday before to the Tuesday after
 * a Monday occurrence.
 */
export interface ScheduleCalendarRule extends ScheduleLeewayWindow {
    repeat: ScheduleRepeat;

    /** daily */
    dailyPreset?: ScheduleDailyPreset;
    /** everyNDays interval, >= 2 (interval 1 is the everyDay preset). */
    dailyInterval?: number;

    /** weekly / fortnightly / seasonal-weekly */
    daysOfWeek?: ScheduleWeekday[];
    /** weekly only: every N weeks, default 1. Fortnightly is fixed at 2. */
    weeklyInterval?: number;
    /**
     * weekly / fortnightly, TEMPLATE tasks only: "N days a week, the days chosen per plan". When set,
     * `daysOfWeek` and `weekSelector` are SUGGESTIONS the create-from-template step prefills, and the
     * plan's own picks replace them (see schedule.template.ts). A live task always holds concrete
     * weekdays, so `validateTaskSchedule` refuses this key unless asked to judge a template.
     */
    daysPerWeek?: number;
    /**
     * fortnightly only. 'A'/'B': anchored 14-day cadence — DTSTART on the effective-from week (A) or 7
     * days later (B); never breaks rhythm, drifts off calendar week numbers across 53-week ISO years.
     * 'isoOdd'/'isoEven': the wall calendar governs — ISO week-of-year parity (government contracts),
     * which repeats a parity across a 53-week year boundary instead of keeping a strict fortnight.
     */
    weekSelector?: ScheduleWeekCycle | 'isoOdd' | 'isoEven';

    /** monthly / quarterly / yearly */
    monthlyOn?: ScheduleMonthlyOn;
    /** One or more of 1–31 or 'last' (e.g. [1, 15] for 1st and 15th). */
    daysOfMonth?: (number | 'last')[];
    /** One or more ordinals sharing `weekday` (e.g. [1, 3] for 1st and 3rd Monday). */
    nths?: ScheduleNth[];
    weekday?: ScheduleWeekday;
    /** quarterly family: every N months (2 | 3 | 4 | 6), default 3. */
    monthInterval?: number;

    /**
     * Month filter (1 = Jan … 12 = Dec).
     * yearly: the month(s) the occurrence lands in (1 or 2 entries).
     * any other repeat: seasonal restriction — occurrences only in these months.
     */
    months?: number[];

    ends: ScheduleEnds;

    /** What happens when an occurrence lands outside the working week or on a holiday. Default 'keep'. */
    nonWorkingDayPolicy: ScheduleNonWorkingDayPolicy;
}

/**
 * How far apart consecutive completed Visits of one task should be, in days.
 *
 * Reporting only: a gap outside `[minDays, maxDays]` is a spacing breach in the KPI and Service history,
 * never a block, so a contract like "twice a month" can still be delivered late. Authored on the task's
 * period-target report rule (plan section 19), not on the schedule: it judges completions, not due dates.
 */
export interface ScheduleSpacing {
    minDays?: number;
    maxDays?: number;
}

/**
 * The weekdays one plan chose for a template task that says "N days a week".
 *
 * `weekSelector` is the fortnight half and is required for a fortnightly task; a weekly task has none.
 */
export interface ScheduleDayPick {
    daysOfWeek: ScheduleWeekday[];
    weekSelector?: ScheduleWeekCycle;
}

export interface ScheduleRule {
    kind: ScheduleKind;
    perVisit?: SchedulePerVisitRule;
    calendar?: ScheduleCalendarRule;
    afterService?: ScheduleAfterServiceRule;
    dates?: ScheduleDatesRule;
}

/**
 * The plain-language cadence a board shows for a schedule (WBS-13206), in band order Daily → Annually.
 *
 * Declaration order IS the sort order: boards sort by `Object.values(ScheduleFrequencyBand)` index, never
 * alphabetically, so reordering these members reorders every Frequency column. The values are the wire
 * values and the labels at once, shared by the board, its filter picker and the Client Portal CSVs.
 */
export enum ScheduleFrequencyBand {
    Daily = 'Daily',
    Weekly = 'Weekly',
    Fortnightly = 'Fortnightly',
    Monthly = 'Monthly',
    Quarterly = 'Quarterly',
    BiAnnually = 'Bi-Annually',
    Annually = 'Annually',
}
